import type { Content, Goal, Session } from '@app-types/domain';
import { toLocalDateIso, todayLocalIso } from '@core/dates';
import { formatDuration } from '@core/duration';
import { PlanModal } from '@ui/components/PlanModal';
import { ReviewPanel } from '@ui/components/ReviewPanel';
import { navigate } from '@ui/lib/router';
import { useStore } from '@ui/lib/useStore';
import { useState } from 'preact/hooks';

function todaySessions(sessions: Session[]): Session[] {
  const today = todayLocalIso();
  return sessions.filter((s) => toLocalDateIso(s.planned_at) === today);
}

function completedThisWeek(sessions: Session[]): Session[] {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - dayOfWeek);
  weekStart.setHours(0, 0, 0, 0);
  return sessions.filter(
    (s) => s.status === 'completed' && s.completed_at && new Date(s.completed_at) >= weekStart,
  );
}

function totalHoursThisWeek(sessions: Session[]): number {
  return completedThisWeek(sessions).reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
}

function streak(sessions: Session[]): number {
  const done = sessions.filter((s) => s.status === 'completed' && s.completed_at);
  if (done.length === 0) return 0;
  const days = new Set(done.map((s) => toLocalDateIso(s.completed_at as string)));
  let s = 0;
  const cur = new Date();
  cur.setHours(12, 0, 0, 0);
  for (let i = 0; i < 400; i++) {
    const key = toLocalDateIso(cur);
    if (days.has(key)) s++;
    else if (i > 0) break;
    cur.setDate(cur.getDate() - 1);
  }
  return s;
}

export function HomeScreen() {
  const contents = useStore((s) => s.contents);
  const sessions = useStore((s) => s.sessions);
  const goals = useStore((s) => s.goals);
  const settings = useStore((s) => s.settings);
  const [planning, setPlanning] = useState(false);

  const activeGoal: Goal | undefined =
    goals.find((g) => g.id === settings.active_goal_id) ||
    goals.find((g) => g.priority === 1 && g.status === 'active') ||
    goals[0];

  const today = todaySessions(sessions);
  const doneToday = today.filter((s) => s.status === 'completed');
  const nextPlanned = today.find((s) => s.status === 'planned' || s.status === 'active');
  const nextContent: Content | undefined = nextPlanned
    ? contents.find((c) => c.id === nextPlanned.content_id)
    : undefined;

  const weekHours = totalHoursThisWeek(sessions);
  const curStreak = streak(sessions);
  const todoContents = contents.filter((c) => c.status === 'todo').length;
  const doneContents = contents.filter((c) => c.status === 'done').length;

  const hasData = contents.length > 0 || goals.length > 0;

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Hoje</h1>
          <div class="main-sub">
            {new Date().toLocaleDateString('pt-BR', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </div>
        </div>
        <div class="main-actions">
          <button class="btn" onClick={() => navigate('contents')}>
            + CONTEÚDO
          </button>
          <button class="btn gold" onClick={() => setPlanning(true)}>
            ◈ GERAR PLANO
          </button>
          {nextPlanned ? (
            <button class="btn primary" onClick={() => navigate('pomodoro', nextPlanned.id)}>
              ▶ INICIAR PRÓXIMA
            </button>
          ) : (
            <button class="btn primary" onClick={() => navigate('pomodoro')}>
              ▶ POMODORO LIVRE
            </button>
          )}
        </div>
      </div>

      {!hasData && (
        <div class="panel" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div class="empty-state">
            <div class="ico">◉</div>
            <h3>Bem-vindo ao Robox</h3>
            <div style={{ marginBottom: '20px' }}>
              Começa por definir sua meta principal e adicionar conteúdos de estudo.
            </div>
            <div
              style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}
            >
              <button class="btn primary lg" onClick={() => navigate('goals')}>
                🎯 CRIAR META
              </button>
              <button class="btn lg" onClick={() => navigate('contents')}>
                📚 ADICIONAR CONTEÚDOS
              </button>
            </div>
          </div>
        </div>
      )}

      {hasData && (
        <>
          <ReviewPanel />

          {/* KPIs */}
          <div class="grid-4" style={{ marginBottom: '20px' }}>
            <div class="kpi">
              <div class="label">Meta ativa</div>
              <div class="value" style={{ fontSize: '18px' }}>
                {activeGoal ? activeGoal.title : '—'}
              </div>
              <div class="sub">{activeGoal?.status || 'sem meta'}</div>
            </div>
            <div class="kpi ok">
              <div class="label">Hoje</div>
              <div class="value">
                {doneToday.length}/{today.length}
              </div>
              <div class="sub">sessões</div>
            </div>
            <div class="kpi purple">
              <div class="label">Semana</div>
              <div class="value">{weekHours.toFixed(1)}h</div>
              <div class="sub">tempo focado</div>
            </div>
            <div class="kpi pink">
              <div class="label">Streak</div>
              <div class="value">{curStreak}d</div>
              <div class="sub">dias consecutivos</div>
            </div>
          </div>

          <div class="grid-2">
            {/* Next action */}
            <div class="panel">
              <div class="panel-title">
                ▰ <b>PRÓXIMA AÇÃO</b>
              </div>
              {nextContent && nextPlanned ? (
                <div>
                  <div style={{ fontSize: '18px', color: 'var(--text)', marginBottom: '8px' }}>
                    {nextContent.title}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    <span class="tag">{nextContent.type}</span>
                    <span class="tag purple">{formatDuration(nextContent.duration_min)}</span>
                    <span class="tag amber">dif. {nextContent.difficulty}</span>
                  </div>
                  <button
                    class="btn primary lg"
                    onClick={() => navigate('pomodoro', nextPlanned.id)}
                  >
                    ▶ INICIAR AGORA
                  </button>
                </div>
              ) : (
                <div class="empty-state" style={{ padding: '20px 0' }}>
                  <div>Sem sessões planejadas para hoje.</div>
                  <button
                    class="btn ghost sm"
                    style={{ marginTop: '10px' }}
                    onClick={() => navigate('pomodoro')}
                  >
                    ▶ POMODORO LIVRE
                  </button>
                </div>
              )}
            </div>

            {/* Progress overview */}
            <div class="panel">
              <div class="panel-title">
                ▰ <b>PROGRESSO DE CONTEÚDOS</b>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontFamily: 'var(--f-mono)',
                    fontSize: '11px',
                    color: 'var(--text-dim)',
                  }}
                >
                  <span>
                    {doneContents} de {contents.length} feitos
                  </span>
                  <span>
                    {contents.length > 0 ? ((doneContents / contents.length) * 100).toFixed(0) : 0}%
                  </span>
                </div>
                <div
                  style={{
                    height: '10px',
                    background: 'rgba(0,8,24,0.6)',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-soft)',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${contents.length > 0 ? (doneContents / contents.length) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, var(--cyan), var(--purple))',
                      transition: 'width 0.5s ease',
                    }}
                  />
                </div>
              </div>
              <div
                style={{ fontFamily: 'var(--f-mono)', fontSize: '11px', color: 'var(--text-dim)' }}
              >
                {todoContents} pendentes · {contents.length} total
              </div>
              <button
                class="btn ghost sm"
                style={{ marginTop: '14px' }}
                onClick={() => navigate('contents')}
              >
                → VER TODOS
              </button>
            </div>
          </div>

          {/* Sessões de hoje */}
          {today.length > 0 && (
            <div class="panel" style={{ marginTop: '16px' }}>
              <div class="panel-title">
                ▰ <b>SESSÕES DE HOJE</b>
                <span style={{ fontFamily: 'var(--f-mono)', fontSize: '10px' }}>
                  {doneToday.length}/{today.length} feitas
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {today
                  .slice()
                  .sort((a, b) => a.planned_at.localeCompare(b.planned_at))
                  .map((s) => {
                    const c = contents.find((x) => x.id === s.content_id);
                    const time = new Date(s.planned_at).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const isDone = s.status === 'completed';
                    const isAbandoned = s.status === 'abandoned';
                    return (
                      <div
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          borderRadius: 'var(--r-sm)',
                          background: 'rgba(0,8,24,0.4)',
                          border: '1px solid var(--border-soft)',
                          opacity: isDone || isAbandoned ? 0.55 : 1,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--f-mono)',
                            fontSize: '11px',
                            color: 'var(--cyan)',
                            minWidth: '42px',
                          }}
                        >
                          {time}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            fontSize: '14px',
                            color: 'var(--text)',
                            textDecoration: isDone ? 'line-through' : 'none',
                          }}
                        >
                          {c?.title || '—'}
                        </span>
                        <span class="tag purple">{formatDuration(s.planned_duration_min)}</span>
                        {isDone ? (
                          <span class="tag green">✓ FEITA</span>
                        ) : isAbandoned ? (
                          <span class="tag dim">abandonada</span>
                        ) : (
                          <button class="btn sm" onClick={() => navigate('pomodoro', s.id)}>
                            ▶ INICIAR
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}

      <PlanModal open={planning} onClose={() => setPlanning(false)} />
    </div>
  );
}
